import { useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Link,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Interview, Facet, DecisionType } from '@/types/interviewer'

interface DecisionTimelineProps {
  interviews: Interview[]
  onSelectInterview?: (interview: Interview) => void
}

const DECISION_TYPE_CONFIG: Record<DecisionType, {
  label: string
  color: string
  description: string
}> = {
  strategic: {
    label: 'Strategic',
    color: '#4a90a4',
    description: 'Long-term direction',
  },
  tactical: {
    label: 'Tactical',
    color: '#5c8a6b',
    description: 'Short-term execution',
  },
  technical: {
    label: 'Technical',
    color: '#6c757d',
    description: 'Architecture/implementation',
  },
  process: {
    label: 'Process',
    color: '#c9a227',
    description: 'How we work',
  },
  tradeoff: {
    label: 'Tradeoff',
    color: '#dc3545',
    description: 'Explicit compromise',
  },
}

interface DecisionEntry {
  facet: Facet
  interview: Interview
  date: Date
  relatedFacets: Facet[]
}

export function DecisionTimeline({
  interviews,
  onSelectInterview,
}: DecisionTimelineProps) {
  // Extract all decisions and sort by date
  const decisions = useMemo(() => {
    const entries: DecisionEntry[] = []

    for (const interview of interviews) {
      if (interview.mode !== 'decisions' || interview.status !== 'analyzed') continue

      const decisionFacets = interview.facets.filter(
        f => f.type === 'decision' && !f.dismissed
      )

      for (const facet of decisionFacets) {
        // Find related facets (alternatives, rationale, constraints)
        const relatedFacets = interview.facets.filter(f =>
          !f.dismissed &&
          ['alternative', 'rationale', 'constraint', 'stakeholder', 'dependency'].includes(f.type)
        )

        entries.push({
          facet,
          interview,
          date: new Date(interview.createdAt),
          relatedFacets,
        })
      }
    }

    return entries.sort((a, b) => b.date.getTime() - a.date.getTime())
  }, [interviews])

  if (decisions.length === 0) {
    return (
      <Card className="p-6 text-center">
        <CheckSquare className="w-10 h-10 text-text-muted mx-auto mb-3" />
        <h3 className="font-medium mb-1">No decisions recorded</h3>
        <p className="text-sm text-text-muted">
          Add decision interviews to build your decision timeline
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Decision Timeline</h3>
        <span className="text-xs text-text-muted">{decisions.length} decisions</span>
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-warm-200" />

        {/* Decision entries */}
        <div className="space-y-4">
          {decisions.map((entry, idx) => (
            <DecisionCard
              key={`${entry.interview.id}-${entry.facet.id}`}
              entry={entry}
              isFirst={idx === 0}
              onClick={() => onSelectInterview?.(entry.interview)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function DecisionCard({
  entry,
  isFirst,
  onClick,
}: {
  entry: DecisionEntry
  isFirst: boolean
  onClick?: () => void
}) {
  const { facet, interview, relatedFacets } = entry
  const typeConfig = facet.decisionType
    ? DECISION_TYPE_CONFIG[facet.decisionType]
    : null

  const alternatives = relatedFacets.filter(f => f.type === 'alternative')
  const rationales = relatedFacets.filter(f => f.type === 'rationale')
  const constraints = relatedFacets.filter(f => f.type === 'constraint')
  const stakeholders = relatedFacets.filter(f => f.type === 'stakeholder')

  return (
    <div className="relative pl-10">
      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-2 w-5 h-5 rounded-full border-2 border-background flex items-center justify-center',
          isFirst ? 'bg-solution' : 'bg-warm-300'
        )}
      >
        <CheckSquare className="w-3 h-3 text-white" />
      </div>

      <Card
        className={cn(
          'p-4 cursor-pointer hover:bg-warm-50 transition-colors',
          facet.reversibility === 'one-way-door' && 'border-l-4 border-l-problem'
        )}
        onClick={onClick}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium">{facet.content}</p>
            <div className="flex items-center gap-2 mt-1 text-xs text-text-muted">
              <span>{interview.title}</span>
              <span>•</span>
              <span>{entry.date.toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {facet.reversibility === 'one-way-door' && (
              <Badge
                variant="outline"
                className="text-xs gap-1 bg-problem/10"
                style={{ borderColor: 'var(--color-problem)', color: 'var(--color-problem)' }}
              >
                <AlertTriangle className="w-3 h-3" />
                One-way door
              </Badge>
            )}
            {typeConfig && (
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: typeConfig.color, color: typeConfig.color }}
              >
                {typeConfig.label}
              </Badge>
            )}
          </div>
        </div>

        {/* Related context */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          {rationales.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <Lightbulb className="w-3 h-3 mt-0.5 text-solution flex-shrink-0" />
              <span className="text-text-secondary line-clamp-2">
                {rationales[0].content}
              </span>
            </div>
          )}

          {alternatives.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <GitBranch className="w-3 h-3 mt-0.5 text-text-muted flex-shrink-0" />
              <span className="text-text-secondary">
                {alternatives.length} alternative{alternatives.length > 1 ? 's' : ''} considered
              </span>
            </div>
          )}

          {constraints.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <Lock className="w-3 h-3 mt-0.5 text-problem flex-shrink-0" />
              <span className="text-text-secondary">
                {constraints.length} constraint{constraints.length > 1 ? 's' : ''}
              </span>
            </div>
          )}

          {stakeholders.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <Link className="w-3 h-3 mt-0.5 text-business flex-shrink-0" />
              <span className="text-text-secondary">
                {stakeholders.map(s => s.content).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Scores */}
        {interview.scores.rationaleQuality !== undefined && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-warm-200 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-text-muted">Rationale:</span>
              <span className={cn(
                'font-medium',
                interview.scores.rationaleQuality >= 4 ? 'text-business' :
                interview.scores.rationaleQuality >= 3 ? 'text-warning' : 'text-problem'
              )}>
                {interview.scores.rationaleQuality}/5
              </span>
            </div>
            {interview.scores.contextPreservation !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-text-muted">Context:</span>
                <span className={cn(
                  'font-medium',
                  interview.scores.contextPreservation >= 4 ? 'text-business' :
                  interview.scores.contextPreservation >= 3 ? 'text-warning' : 'text-problem'
                )}>
                  {interview.scores.contextPreservation}/5
                </span>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
